import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, Car, User, LayoutDashboard, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { NAV_LINKS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const DASH_FOR = { customer: "/dashboard", driver: "/driver", operator: "/driver", agent: "/agent", admin: "/admin", super_admin: "/admin", support: "/agent" };

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const dash = user ? DASH_FOR[user.role] || "/dashboard" : null;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/95 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy text-white"><Car size={18} /></span>
          <span className="font-heading text-lg font-bold tracking-tight text-navy">SmartFares<span className="text-brand">4U</span></span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.to.replace(/\//g, "")}`}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition hover:bg-slate-100 ${loc.pathname === l.to ? "text-brand" : "text-slate-600"}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="user-menu-btn" className="gap-2">
                  <User size={16} /> <span className="hidden sm:inline max-w-[120px] truncate">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => navigate(dash)} data-testid="menu-dashboard">
                  <LayoutDashboard size={15} className="mr-2" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/messages")} data-testid="menu-messages">Messages</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut size={15} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" size="sm" className="hidden sm:inline-flex" onClick={() => navigate("/login")} data-testid="login-btn">Login</Button>
              <Button variant="outline" size="sm" className="hidden sm:inline-flex" onClick={() => navigate("/register")} data-testid="signup-btn">Sign Up</Button>
            </>
          )}
          <Button size="sm" className="bg-brand hover:bg-brand/90" onClick={() => navigate("/search")} data-testid="book-now-btn">Book Now</Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="lg:hidden" data-testid="mobile-menu-btn"><Menu size={18} /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetHeader><SheetTitle className="font-heading">Menu</SheetTitle></SheetHeader>
              <div className="mt-6 flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mnav-${l.to.replace(/\//g, "")}`}
                    className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">{l.label}</Link>
                ))}
                <div className="my-2 h-px bg-slate-200" />
                {!user && <>
                  <Link to="/login" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">Login</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">Sign Up</Link>
                </>}
                {user && <Link to={dash} onClick={() => setOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-brand hover:bg-slate-100">Dashboard</Link>}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
